import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { Credential, CredentialGroup, PlanPreviewStep, PlanProblem } from '../api/types'
import { Card, ErrorBanner, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import {
  brokenConditions,
  describeCondition,
  expensiveWithoutFilter,
  factsAvailableAt,
  freeStepId,
  moveStep,
  operatorsFor,
  paramFromText,
  paramToText,
  planProduces,
  removeStep,
  stepsAvailableAt,
  suggestedPlan,
  type Condition,
  type PlanFact,
  type PlanParam,
  type PlanStep,
  type PlanStepKind,
  type StepCatalogue,
} from '../planEditor'
import { t } from '../i18n/lingua'

/**
 * L'editor di un piano di discovery.
 *
 * ## Cosa fa questa pagina che nessun'altra può fare
 *
 * Un piano si legge dall'alto in basso e **si comporta a rami**. Leggerlo non
 * basta a sapere cosa farà su un apparato che risponde a SNMP e non a SSH: per
 * questo l'editor mostra, accanto a ogni passo, i fatti che a quel punto
 * esistono davvero, e ha una prova a secco che dice quali passi partirebbero.
 *
 * ## La divisione del lavoro
 *
 * La logica sta in `planEditor.ts`, non qui. In questo progetto è già successo
 * che un difetto sopravvivesse a due giri di correzioni perché la logica era
 * dentro un componente e non era verificabile da nessuna parte.
 *
 * Il **verdetto** è del Control Plane: questa pagina chiama `/validate` e mostra
 * la risposta. Riprodurre la validazione qui darebbe due giudici che divergono.
 */
export default function DiscoveryPlanEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'nuovo'

  const catalogueQuery = useAsync(() => api.planSteps(), [])
  // Le credenziali servono a **scegliere**, non a digitare: il campo era una
  // casella di testo che chiedeva gli identificativi separati da virgola, cioè
  // degli UUID. Nessuno li sa a memoria, e sbagliarne uno non dà errore al
  // salvataggio — il passo fallisce su ogni bersaglio, in campo, con «accesso
  // negato», che manda a controllare la credenziale invece del piano.
  const credentials = useAsync(() => api.credentials(), [])
  const credentialGroups = useAsync(() => api.credentialGroups(), [])
  const existing = useAsync(
    () => (isNew ? Promise.resolve(null) : api.discoveryPlan(id!)),
    [id, isNew],
  )

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [targets, setTargets] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [steps, setSteps] = useState<PlanStep[]>([])
  const [loaded, setLoaded] = useState(false)

  const [problems, setProblems] = useState<PlanProblem[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const catalogue = catalogueQuery.data

  // Carica una volta sola: rimettere i campi a ogni render riscriverebbe
  // quello che si sta scrivendo.
  useEffect(() => {
    if (loaded || !catalogue) return
    if (isNew) {
      setSteps(suggestedPlan(catalogue))
      setName(t('Discovery di rete'))
      setLoaded(true)
      return
    }
    if (existing.data) {
      const plan = existing.data
      setName(plan.name)
      setDescription(plan.description ?? '')
      setTargets((plan.default_targets ?? []).join('\n'))
      setIsDefault(plan.is_default)
      setSteps(plan.steps ?? [])
      setLoaded(true)
    }
  }, [catalogue, existing.data, isNew, loaded])

  // Il verdetto arriva dal server, mentre si scrive.
  //
  // Con un ritardo: validare a ogni tasto manderebbe una richiesta per lettera,
  // e le risposte tornerebbero fuori ordine mostrando il giudizio su un piano
  // che non è più quello sullo schermo.
  useEffect(() => {
    if (!loaded || steps.length === 0) {
      setProblems([])
      return
    }
    const handle = setTimeout(() => {
      api
        .validatePlan({ name: name || 'senza nome', steps })
        .then((result) => setProblems(result.problems))
        .catch(() => {
          /* La validazione è un aiuto: se il server non risponde, si continua a
             scrivere. Il salvataggio rifiuterà comunque un piano non eseguibile. */
        })
    }, 400)
    return () => clearTimeout(handle)
  }, [steps, name, loaded])

  const broken = useMemo(
    () => (catalogue ? brokenConditions(steps, catalogue) : []),
    [steps, catalogue],
  )
  const expensive = useMemo(
    () => (catalogue ? expensiveWithoutFilter(steps, catalogue) : []),
    [steps, catalogue],
  )

  const update = useCallback((index: number, next: PlanStep) => {
    setSteps((current) => current.map((step, i) => (i === index ? next : step)))
  }, [])

  if (catalogueQuery.loading || existing.loading) return <Spinner />
  if (catalogueQuery.error) return <ErrorBanner error={catalogueQuery.error} />
  // Un piano che non si carica **non** diventa un modulo vuoto.
  //
  // Un modulo vuoto è anche l'aspetto di un piano nuovo: chi lo vedeva
  // ricominciava a scriverlo, salvava, e sovrascriveva il piano vero con
  // quello che aveva davanti. Il difetto non falliva — cancellava.
  if (!isNew && existing.error) {
    return (
      <>
        <ErrorBanner error={existing.error} />
        <p className="text-muted">
          {t("Il piano non è stato caricato: non è stato modificato niente. Ricarica la pagina; se l'errore resta, il piano potrebbe essere stato eliminato.")}
        </p>
      </>
    )
  }
  if (!catalogue) return null

  const errors = problems.filter((p) => p.severity === 'error')
  const warnings = problems.filter((p) => p.severity === 'warning')

  async function save() {
    setSaving(true)
    setSaveError(null)
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      steps,
      default_targets: targets
        .split(/[\n,]/)
        .map((t) => t.trim())
        .filter(Boolean),
      is_default: isDefault,
    }
    try {
      if (isNew) {
        const created = await api.createPlan(payload)
        navigate(`/discovery-plans/${created.id}`, { replace: true })
      } else {
        await api.updatePlan(id!, payload)
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="page">
      <Card title={isNew ? t('Nuovo piano di discovery') : t('Piano «{nome}»', { nome: name })}>
        <p className="text-muted">
          {t('La sequenza di passi che la sonda esegue su ogni indirizzo dell’ambito. Ogni passo può dipendere da ciò che i precedenti hanno scoperto.')}
        </p>
        <div className="kindform">
          <label>
            {t('Nome')}
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            {t('Descrizione')}
            <input value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label style={{ gridColumn: '1 / -1' }}>
            {t('Ambiti predefiniti — subnet, intervalli o indirizzi, uno per riga')}
            <textarea
              rows={3}
              value={targets}
              placeholder="10.0.0.0/24&#10;192.168.1.10-192.168.1.50"
              onChange={(e) => setTargets(e.target.value)}
            />
            <span className="text-muted">
              {t('Sono un suggerimento: chi avvia la scansione può sostituirli, ed è il motivo per cui lo stesso piano si riusa su reti diverse.')}
            </span>
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            {t('Proponi questo piano per primo')}
          </label>
        </div>
      </Card>

      {(errors.length > 0 || warnings.length > 0 || broken.length > 0) && (
        <Card title={t('Cosa non torna')}>
          {/* I messaggi dicono cosa succederebbe, non solo cosa è sbagliato:
              «condizione non valida» manda a indovinare. */}
          {errors.map((problem, i) => (
            <p key={`e${i}`} className="banner banner--error">
              {problem.step_id ? <strong>{problem.step_id}: </strong> : null}
              {problem.message}
            </p>
          ))}
          {broken.map((problem, i) => (
            <p key={`b${i}`} className="banner banner--error">
              <strong>{problem.stepId}: </strong>
              {problem.reason}
            </p>
          ))}
          {warnings.map((problem, i) => (
            <p key={`w${i}`} className="banner banner--warn">
              {problem.step_id ? <strong>{problem.step_id}: </strong> : null}
              {problem.message}
            </p>
          ))}
          {expensive.map((stepId) => (
            <p key={`x${stepId}`} className="banner banner--warn">
              <strong>{stepId}: </strong>
              {t('passo costoso senza un filtro davanti: verrebbe provato su ogni indirizzo dell’ambito, compresi quelli spenti.')}
            </p>
          ))}
        </Card>
      )}

      <Card title={t('Passi ({n} su {max})', { n: steps.length, max: catalogue.max_steps })}>
        <p className="text-muted">{t('Si eseguono nell’ordine, su ogni indirizzo separatamente.')}</p>
        {steps.length === 0 && (
          <p className="text-muted">
            {t('Un piano senza passi non raccoglierebbe niente. Aggiungine uno qui sotto.')}
          </p>
        )}
        <ol className="plan-steps">
          {steps.map((step, index) => (
            <li key={`${step.id}-${index}`}>
              <StepCard
                step={step}
                index={index}
                total={steps.length}
                catalogue={catalogue}
                available={factsAvailableAt(steps, index, catalogue)}
                earlier={stepsAvailableAt(steps, index)}
                broken={broken.filter((b) => b.stepId === step.id)}
                credentials={credentials.data ?? []}
                credentialGroups={credentialGroups.data ?? []}
                onChange={(next) => update(index, next)}
                onMove={(direction) => setSteps(moveStep(steps, index, direction, catalogue).steps)}
                onRemove={() => setSteps(removeStep(steps, index, catalogue).steps)}
              />
            </li>
          ))}
        </ol>

        <AddStep
          catalogue={catalogue}
          disabled={steps.length >= catalogue.max_steps}
          onAdd={(probe) =>
            setSteps((current) => [
              ...current,
              { id: freeStepId(current, probe), probe, credential_refs: [] },
            ])
          }
        />
      </Card>

      {!isNew && <Preview planId={id!} steps={steps} catalogue={catalogue} />}

      {saveError && <ErrorBanner error={saveError} />}

      <div className="rowactions">
        <button
          className="chip"
          disabled={saving || errors.length > 0 || broken.length > 0 || !name.trim()}
          onClick={save}
        >
          {saving ? t('Salvataggio…') : isNew ? t('Crea il piano') : t('Salva')}
        </button>
        <Link className="chip" to="/discovery-plans">
          {t('Torna all’elenco')}
        </Link>
        {errors.length > 0 && (
          <span className="text-muted">
            {t('Il piano non è eseguibile: gli errori qui sopra vanno risolti. Gli avvisi no — un ordine costoso è una scelta.')}
          </span>
        )}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ un passo

function StepCard({
  step,
  index,
  total,
  catalogue,
  available,
  earlier,
  broken,
  credentials,
  credentialGroups,
  onChange,
  onMove,
  onRemove,
}: {
  step: PlanStep
  index: number
  total: number
  catalogue: StepCatalogue
  available: PlanFact[]
  earlier: PlanStep[]
  broken: { stepId: string; reason: string }[]
  credentials: Credential[]
  credentialGroups: CredentialGroup[]
  onChange: (next: PlanStep) => void
  onMove: (direction: -1 | 1) => void
  onRemove: () => void
}) {
  const kind = catalogue.steps.find((k) => k.key === step.probe)

  return (
    <div className={`plan-step${broken.length ? ' plan-step--broken' : ''}`}>
      <header>
        <span className="plan-step-order">{index + 1}</span>
        <div>
          <strong>{kind?.label ? t(kind.label) : step.probe}</strong>
          <span className="badge badge--muted">{t('costo {c}', { c: kind?.cost })}</span>
          <p className="text-muted">{kind?.hint}</p>
        </div>
        <div className="plan-step-actions">
          <button className="chip" disabled={index === 0} onClick={() => onMove(-1)}>
            ↑
          </button>
          <button className="chip" disabled={index === total - 1} onClick={() => onMove(1)}>
            ↓
          </button>
          <button className="chip chip--muted" onClick={onRemove}>
            {t('togli')}
          </button>
        </div>
      </header>

      <div className="kindform">
        <label>
          {t('Identificativo')}
          <input value={step.id} onChange={(e) => onChange({ ...step, id: e.target.value })} />
          <span className="text-muted">{t('Le condizioni degli altri passi lo citano con questo nome.')}</span>
        </label>
        {kind?.requires_credential && (
          <StepCredentials
            step={step}
            kind={kind}
            credentials={credentials}
            credentialGroups={credentialGroups}
            onChange={onChange}
          />
        )}
        <label className="checkbox">
          <input
            type="checkbox"
            checked={step.continue_on_failure !== false}
            onChange={(e) => onChange({ ...step, continue_on_failure: e.target.checked })}
          />
          {t('Se fallisce, prosegui con i passi successivi')}
          <span className="text-muted">
            {t('Vale per quel bersaglio soltanto: su una scansione di rete il fallimento su un indirizzo è il caso normale.')}
          </span>
        </label>
      </div>

      <ConditionEditor
        step={step}
        available={available}
        earlier={earlier}
        onChange={(when) => onChange({ ...step, when })}
      />

      {broken.map((problem, i) => (
        <p key={i} className="banner banner--error">
          {problem.reason}
        </p>
      ))}

      <details>
        <summary>{t('Parametri')}</summary>
        <ParamsEditor step={step} kind={kind} onChange={onChange} />
      </details>
    </div>
  )
}

// -------------------------------------------------------------- la condizione

/**
 * L'editor della condizione.
 *
 * Offre **solo** i fatti già disponibili a questo punto del piano e gli esiti dei
 * passi precedenti. È il vincolo che impedisce l'errore più comune e più
 * silenzioso: condizionare su qualcosa che a quel punto non esiste ancora,
 * scrivendo un passo che non parte mai.
 */
function ConditionEditor({
  step,
  available,
  earlier,
  onChange,
}: {
  step: PlanStep
  available: PlanFact[]
  earlier: PlanStep[]
  onChange: (when: Condition | null) => void
}) {
  const parts = conditionParts(step.when)
  const combinator = Array.isArray((step.when as Condition)?.any) ? 'any' : 'all'

  function setParts(next: Condition[], mode: 'all' | 'any' = combinator) {
    if (next.length === 0) return onChange(null)
    if (next.length === 1) return onChange(next[0])
    onChange({ [mode]: next })
  }

  if (available.length === 0 && earlier.length === 0) {
    return (
      <p className="text-muted">
        {t('È il primo passo: non c’è ancora niente su cui condizionare, quindi parte sempre.')}
      </p>
    )
  }

  return (
    <div className="condition-editor">
      <div className="condition-header">
        <strong>{t('Esegui questo passo')}</strong>
        {parts.length === 0 ? (
          <span className="text-muted">{t('sempre')}</span>
        ) : (
          <>
            <span className="text-muted">{t('se')}</span>
            {parts.length > 1 && (
              <select
                value={combinator}
                onChange={(e) => setParts(parts, e.target.value as 'all' | 'any')}
              >
                <option value="all">{t('tutte le condizioni')}</option>
                <option value="any">{t('almeno una')}</option>
              </select>
            )}
          </>
        )}
      </div>

      {parts.map((part, i) => (
        <ConditionRow
          key={i}
          part={part}
          available={available}
          earlier={earlier}
          onChange={(next) => setParts(parts.map((p, j) => (j === i ? next : p)))}
          onRemove={() => setParts(parts.filter((_, j) => j !== i))}
        />
      ))}

      <div className="condition-add">
        <button
          className="chip"
          onClick={() =>
            setParts([
              ...parts,
              available.length > 0
                ? defaultConditionFor(available[0])
                : { step: earlier[earlier.length - 1].id, status: 'ok' },
            ])
          }
        >
          {t('+ condizione')}
        </button>
        {parts.length > 0 && (
          <span className="text-muted">
            {describeCondition(step.when, available)}
          </span>
        )}
      </div>
    </div>
  )
}

function ConditionRow({
  part,
  available,
  earlier,
  onChange,
  onRemove,
}: {
  part: Condition
  available: PlanFact[]
  earlier: PlanStep[]
  onChange: (next: Condition) => void
  onRemove: () => void
}) {
  const isStepRef = typeof part.step === 'string'
  const fact = available.find((f) => f.name === part.fact)
  const operator = fact ? currentOperator(part) : null

  return (
    <div className="condition-row">
      <select
        value={isStepRef ? `step:${part.step}` : `fact:${String(part.fact ?? '')}`}
        onChange={(e) => {
          const [kind, value] = e.target.value.split(/:(.*)/)
          if (kind === 'step') return onChange({ step: value, status: 'ok' })
          const chosen = available.find((f) => f.name === value)
          if (chosen) onChange(defaultConditionFor(chosen))
        }}
      >
        {available.length > 0 && (
          <optgroup label={t('Ciò che i passi precedenti hanno scoperto')}>
            {available.map((f) => (
              <option key={f.name} value={`fact:${f.name}`}>
                {f.label}
              </option>
            ))}
          </optgroup>
        )}
        {earlier.length > 0 && (
          <optgroup label={t('Esito di un passo precedente')}>
            {earlier.map((s) => (
              <option key={s.id} value={`step:${s.id}`}>
                {t('il passo «{id}»', { id: s.id })}
              </option>
            ))}
          </optgroup>
        )}
      </select>

      {isStepRef ? (
        <select
          value={String(part.status ?? 'ok')}
          onChange={(e) => onChange({ ...part, status: e.target.value })}
        >
          <option value="ok">{t('è riuscito')}</option>
          <option value="failed">{t('è fallito')}</option>
          <option value="skipped">{t('è stato saltato')}</option>
        </select>
      ) : fact ? (
        <>
          <select
            value={operator ?? 'equals'}
            onChange={(e) => onChange(withOperator(fact, e.target.value, part))}
          >
            {operatorsFor(fact).map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
          <ValueInput fact={fact} operator={operator ?? 'equals'} part={part} onChange={onChange} />
        </>
      ) : (
        <span className="banner banner--error">
          {t('«{fatto}» non è disponibile a questo punto del piano', { fatto: String(part.fact) })}
        </span>
      )}

      <button className="chip chip--muted" onClick={onRemove}>
        ×
      </button>
    </div>
  )
}

function ValueInput({
  fact,
  operator,
  part,
  onChange,
}: {
  fact: PlanFact
  operator: string
  part: Condition
  onChange: (next: Condition) => void
}) {
  if (operator === 'exists') {
    return (
      <select
        value={part.exists === false ? 'no' : 'si'}
        onChange={(e) => onChange({ fact: fact.name, exists: e.target.value === 'si' })}
      >
        <option value="si">{t('sì')}</option>
        <option value="no">{t('no')}</option>
      </select>
    )
  }

  const raw = part[operator]

  if (fact.type === 'bool') {
    return (
      <select
        value={raw === false ? 'false' : 'true'}
        onChange={(e) => onChange({ fact: fact.name, [operator]: e.target.value === 'true' })}
      >
        <option value="true">{t('vero')}</option>
        <option value="false">{t('falso')}</option>
      </select>
    )
  }

  const numeric = fact.type === 'number' || fact.type === 'list_number'
  return (
    <input
      type={numeric ? 'number' : 'text'}
      value={raw === undefined || raw === null ? '' : String(raw)}
      onChange={(e) => {
        const value = numeric ? Number(e.target.value) : e.target.value
        onChange({ fact: fact.name, [operator]: value })
      }}
    />
  )
}

// -------------------------------------------------------------- i parametri

function ParamsEditor({
  step,
  kind,
  onChange,
}: {
  step: PlanStep
  kind?: PlanStepKind
  onChange: (next: PlanStep) => void
}) {
  const declared = kind?.params ?? []
  return (
    <div>
      {declared.map((param) => (
        <DeclaredParam
          key={param.name}
          param={param}
          value={(step.params ?? {})[param.name]}
          onChange={(value) => {
            const next = { ...(step.params ?? {}) }
            // Vuoto **toglie** il parametro invece di scriverci dentro niente:
            // «assente» significa «usa il predefinito», e un `null` salvato al
            // suo posto sarebbe un valore, non un'assenza.
            if (value === undefined) delete next[param.name]
            else next[param.name] = value
            onChange({ ...step, params: next })
          }}
        />
      ))}
      {declared.length > 0 && (
        <details>
          <summary className="text-muted">{t('Altri parametri (JSON)')}</summary>
          <RawParams step={step} onChange={onChange} />
        </details>
      )}
      {declared.length === 0 && <RawParams step={step} onChange={onChange} />}
    </div>
  )
}

/** Un parametro descritto: casella con etichetta, unità e valore predefinito. */
function DeclaredParam({
  param,
  value,
  onChange,
}: {
  param: PlanParam
  value: unknown
  onChange: (value: unknown) => void
}) {
  const [text, setText] = useState(() => paramToText(value))
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="field">
      <label htmlFor={`p-${param.name}`}>{t(param.label)}</label>
      <input
        id={`p-${param.name}`}
        value={text}
        placeholder={param.default ? t('predefinito: {v}', { v: param.default }) : ''}
        onChange={(e) => {
          setText(e.target.value)
          const parsed = paramFromText(param.kind, e.target.value)
          setError(parsed.error ?? null)
          if (!parsed.error) onChange(parsed.value)
        }}
      />
      {error ? (
        <p className="field__hint" style={{ color: 'var(--error)' }}>
          {error}
        </p>
      ) : (
        param.hint && <p className="field__hint">{param.hint}</p>
      )}
    </div>
  )
}

/** La casella di JSON: resta, perché la sonda accetta più di ciò che è descritto. */
function RawParams({
  step,
  onChange,
}: {
  step: PlanStep
  onChange: (next: PlanStep) => void
}) {
  const [text, setText] = useState(() => JSON.stringify(step.params ?? {}, null, 2))
  const [error, setError] = useState<string | null>(null)

  return (
    <div>
      <textarea
        rows={6}
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          try {
            const parsed = JSON.parse(e.target.value || '{}')
            setError(null)
            onChange({ ...step, params: parsed })
          } catch {
            // Non si propaga un JSON incompleto: si sta ancora scrivendo, e
            // aggiornare il passo a ogni carattere lo lascerebbe rotto a metà.
            setError(t('JSON non ancora valido'))
          }
        }}
      />
      {error && <p className="text-muted">{error}</p>}
      <p className="text-muted">
        {t('Porte, timeout, cartelle in cui cercare, comandi della scansione personalizzata. Vuoto significa «usa i valori predefiniti».')}
      </p>
    </div>
  )
}

// ------------------------------------------------------------- prova a secco

/**
 * La prova a secco.
 *
 * Risponde alla domanda che nessun'altra parte della pagina può dare: *su un
 * host fatto così, quali passi partirebbero*. Un piano si comporta a rami, e
 * rileggerlo non basta a saperlo.
 *
 * I fatti li scrive chi prova, perché il punto è immaginare un host — «risponde
 * al ping, ha la 22 aperta, SNMP tace» — e vedere cosa succede.
 */
function Preview({
  planId,
  steps,
  catalogue,
}: {
  planId: string
  steps: PlanStep[]
  catalogue: StepCatalogue
}) {
  const producible = useMemo(() => planProduces(steps, catalogue), [steps, catalogue])
  const [facts, setFacts] = useState<Record<string, unknown>>({})
  const [result, setResult] = useState<PlanPreviewStep[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function run() {
    setError(null)
    try {
      setResult(await api.previewPlan(planId, { facts }))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  // Steps that would not run stay listed, dimmed.
  const attenua = (row: PlanPreviewStep, cella: ReactNode) =>
    row.runs ? cella : <div style={{ opacity: 0.55 }}>{cella}</div>
  const colonneProva: Column<PlanPreviewStep>[] = [
    {
      key: 'step_id',
      header: t('Passo'),
      value: (row) => `${row.step_id} ${row.probe}`,
      render: (row) =>
        attenua(
          row,
          <>
            <strong>{row.step_id}</strong>
            <span className="text-muted"> {row.probe}</span>
          </>,
        ),
    },
    {
      key: 'runs',
      header: t('Parte?'),
      value: (row) => (row.runs ? t('sì') : t('no')),
      render: (row) => attenua(row, row.runs ? t('sì') : t('no')),
    },
    {
      key: 'reason',
      header: t('Perché'),
      render: (row) => attenua(row, <span className="text-muted">{row.reason}</span>),
    },
  ]

  return (
    <Card title={t('Prova a secco')}>
      <p className="text-muted">
        {t('Immagina un host con questi fatti e guarda quali passi partirebbero. Non tocca la rete: usa la stessa valutazione che userà la sonda.')}
      </p>
      <div className="kindform">
        {producible.map((fact) => (
          <label key={fact.name}>
            {fact.label}
            <FactInput
              fact={fact}
              value={facts[fact.name]}
              onChange={(value) =>
                setFacts((current) => {
                  const next = { ...current }
                  if (value === undefined) delete next[fact.name]
                  else next[fact.name] = value
                  return next
                })
              }
            />
          </label>
        ))}
      </div>

      <div className="rowactions">
        <button className="chip" onClick={run}>
          {t('Prova')}
        </button>
        <span className="text-muted">
          {t('Un fatto lasciato vuoto è «non raccolto», e non soddisfa nessuna condizione.')}
        </span>
      </div>

      {error && <ErrorBanner error={error} />}

      {result && (
        // The plan's own order, on one page: it is the sequence the probe follows.
        <DataTable
          rows={result}
          columns={colonneProva}
          rowKey={(row) => row.step_id}
          conteggio={{ nome: t('passi') }}
          paginazione={false}
        />
      )}
    </Card>
  )
}

function FactInput({
  fact,
  value,
  onChange,
}: {
  fact: PlanFact
  value: unknown
  onChange: (value: unknown) => void
}) {
  if (fact.type === 'bool') {
    return (
      <select
        value={value === undefined ? '' : value ? 'true' : 'false'}
        onChange={(e) =>
          onChange(e.target.value === '' ? undefined : e.target.value === 'true')
        }
      >
        <option value="">{t('non raccolto')}</option>
        <option value="true">{t('vero')}</option>
        <option value="false">{t('falso')}</option>
      </select>
    )
  }
  if (fact.type === 'list_number' || fact.type === 'list_string') {
    return (
      <input
        placeholder={t('separati da virgola')}
        value={Array.isArray(value) ? value.join(', ') : ''}
        onChange={(e) => {
          const parts = e.target.value
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
          if (parts.length === 0) return onChange(undefined)
          onChange(fact.type === 'list_number' ? parts.map(Number) : parts)
        }}
      />
    )
  }
  const numeric = fact.type === 'number'
  return (
    <input
      type={numeric ? 'number' : 'text'}
      value={value === undefined ? '' : String(value)}
      onChange={(e) =>
        onChange(e.target.value === '' ? undefined : numeric ? Number(e.target.value) : e.target.value)
      }
    />
  )
}

// ------------------------------------------------------------- aggiungi passo

function AddStep({
  catalogue,
  disabled,
  onAdd,
}: {
  catalogue: StepCatalogue
  disabled: boolean
  onAdd: (probe: string) => void
}) {
  if (disabled) {
    return (
      <p className="text-muted">
        {t('Il piano ha raggiunto il massimo di {n} passi. Oltre quel numero nessuno lo rilegge prima di lanciarlo su una rete di produzione.', { n: catalogue.max_steps })}
      </p>
    )
  }
  return (
    <div className="add-step">
      <strong>{t('Aggiungi un passo')}</strong>
      <div className="filters">
        {catalogue.steps.map((kind) => (
          <button key={kind.key} className="chip" title={kind.hint} onClick={() => onAdd(kind.key)}>
            + {t(kind.label)}
          </button>
        ))}
      </div>
    </div>
  )
}

// --------------------------------------------------------------- conversioni

/** Le condizioni elementari di un passo, appiattite per l'editor. */
function conditionParts(when: Condition | null | undefined): Condition[] {
  if (!when) return []
  if (Array.isArray(when.all)) return when.all as Condition[]
  if (Array.isArray(when.any)) return when.any as Condition[]
  return [when]
}

function currentOperator(part: Condition): string | null {
  for (const op of ['equals', 'not_equals', 'contains', 'exists', 'greater_than', 'less_than']) {
    if (op in part) return op
  }
  return null
}

/** La condizione proposta quando si sceglie un fatto: quella più probabile. */
function defaultConditionFor(fact: PlanFact): Condition {
  switch (fact.type) {
    case 'bool':
      return { fact: fact.name, equals: true }
    case 'list_number':
      return { fact: fact.name, contains: 22 }
    case 'list_string':
      return { fact: fact.name, contains: '' }
    case 'number':
      return { fact: fact.name, greater_than: 0 }
    default:
      return { fact: fact.name, exists: true }
  }
}

/** Cambia operatore conservando il valore quando ha ancora senso. */
function withOperator(fact: PlanFact, operator: string, previous: Condition): Condition {
  if (operator === 'exists') return { fact: fact.name, exists: true }
  const old = currentOperator(previous)
  const carried = old && old !== 'exists' ? previous[old] : undefined
  if (carried === undefined) return defaultConditionFor(fact)
  return { fact: fact.name, [operator]: carried }
}


/**
 * Le credenziali di un passo: si scelgono, e l'ordine conta.
 *
 * La sonda le prova **in sequenza** e si ferma alla prima che risponde. La
 * sequenza non è un dettaglio estetico: ogni tentativo fallito costa un timeout
 * per ogni bersaglio, e con SNMPv3 un'autenticazione fallita resta tale — alcuni
 * apparati bloccano l'utenza dopo qualche tentativo.
 *
 * Prima era una casella di testo in cui scrivere gli UUID separati da virgola.
 * Nessuno li sa a memoria, e un identificativo sbagliato non dà errore al
 * salvataggio: il passo fallisce su ogni bersaglio, in campo.
 */
function StepCredentials({
  step,
  kind,
  credentials,
  credentialGroups,
  onChange,
}: {
  step: PlanStep
  kind: PlanStepKind
  credentials: Credential[]
  credentialGroups: CredentialGroup[]
  onChange: (next: PlanStep) => void
}) {
  const chosen = step.credential_refs ?? []

  // Solo le credenziali che questo passo può usare: una community SNMP su un
  // passo SSH verrebbe provata e rifiutata, cioè un accesso negato nei log di
  // sicurezza del cliente per un errore nostro.
  const usable = credentials.filter((c) => kind.credential_kinds.includes(c.kind))
  const usableGroups = credentialGroups.filter((g) =>
    g.members.some((m) => m.kind && kind.credential_kinds.includes(m.kind)),
  )
  const label = (ref: string) => {
    const single = credentials.find((c) => c.id === ref)
    if (single) return `${single.name} (${single.kind})`
    const group = credentialGroups.find((g) => g.id === ref)
    if (group) return t('{nome} — gruppo', { nome: group.name })
    // Un riferimento che non corrisponde a niente: la credenziale è stata
    // cancellata dopo che il piano era scritto. Dirlo qui è l'unico momento in
    // cui qualcuno può accorgersene prima della scansione.
    return t('{id}… — non esiste più', { id: ref.slice(0, 8) })
  }

  function add(ref: string) {
    if (ref && !chosen.includes(ref)) {
      onChange({ ...step, credential_refs: [...chosen, ref] })
    }
  }
  function move(index: number, direction: -1 | 1) {
    const next = [...chosen]
    const target = index + direction
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange({ ...step, credential_refs: next })
  }

  return (
    <div className="field">
      <label htmlFor={`cred-${step.id}`}>{t('Credenziali')}</label>

      {chosen.length > 0 && (
        <ol className="plan-credentials">
          {chosen.map((ref, index) => (
            <li key={ref}>
              <span>{label(ref)}</span>
              <span className="plan-step-actions">
                <button
                  type="button"
                  className="chip"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="chip"
                  disabled={index === chosen.length - 1}
                  onClick={() => move(index, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="chip chip--muted"
                  onClick={() =>
                    onChange({ ...step, credential_refs: chosen.filter((r) => r !== ref) })
                  }
                >
                  {t('togli')}
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}

      <select id={`cred-${step.id}`} value="" onChange={(e) => add(e.target.value)}>
        <option value="">{t('Aggiungi…')}</option>
        {usableGroups.length > 0 && (
          <optgroup label={t('Gruppi (provati in ordine)')}>
            {usableGroups
              .filter((g) => !chosen.includes(g.id))
              .map((g) => (
                <option key={g.id} value={g.id}>
                  {t('{nome} — {n} credenziali', { nome: g.name, n: g.members.length })}
                </option>
              ))}
          </optgroup>
        )}
        <optgroup label={t('Credenziali')}>
          {usable
            .filter((c) => !chosen.includes(c.id))
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.kind})
              </option>
            ))}
        </optgroup>
      </select>

      <span className="text-muted">
        {chosen.length === 0 ? (
          <>
            {t('Tipi ammessi: {tipi}. Senza, il passo fallirebbe su ogni bersaglio — e il rapporto direbbe «accesso negato», che manda a controllare le credenziali invece di aggiungerne una.', { tipi: kind.credential_kinds.join(', ') })}
          </>
        ) : (
          <>
            {t('Provate in ordine: la sonda si ferma alla prima che risponde. Ogni tentativo fallito costa un timeout per ogni bersaglio.')}
          </>
        )}
        {usable.length === 0 && usableGroups.length === 0 && (
          <>
            {' '}
            {t('Nessuna credenziale di tipo {tipi}.', { tipi: kind.credential_kinds.join(' / ') })}{' '}
            <Link to="/credentials">{t('creane una')}</Link>
          </>
        )}
      </span>
    </div>
  )
}
